/**
 * ExportDialog - Dialog for exporting the final video
 */
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Download, Film, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import { getApiUrl, getApiBaseUrl } from '@/config/api';

const API = getApiUrl();

// Format duration for display
const formatDuration = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
};

export function ExportDialog({ 
  open, 
  onOpenChange, 
  projectId,
  projectName,
  totalDuration,
  clipCount,
  getAuthHeaders 
}) {
  const [format, setFormat] = useState('mp4');
  const [exporting, setExporting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, exporting, completed, failed
  const [error, setError] = useState(null);
  const [outputUrl, setOutputUrl] = useState(null);
  const pollIntervalRef = useRef(null);

  // Clear state when dialog closes
  useEffect(() => {
    if (!open) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      // Reset state after a delay to avoid flicker
      setTimeout(() => {
        setExporting(false);
        setJobId(null);
        setProgress(0);
        setStatus('idle');
        setError(null);
        setOutputUrl(null);
      }, 300);
    }
  }, [open]);

  // Poll for export status
  useEffect(() => {
    if (jobId && status === 'exporting') {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/projects/${projectId}/editor/export/${jobId}/status`, {
            headers: getAuthHeaders()
          });
          
          const job = res.data.job;
          setProgress(job.progress || 0);
          
          if (job.status === 'completed') {
            setStatus('completed');
            setOutputUrl(job.output_url);
            clearInterval(pollIntervalRef.current);
          } else if (job.status === 'failed') {
            setStatus('failed');
            setError(job.error_message || 'Export failed');
            clearInterval(pollIntervalRef.current);
          }
        } catch (err) {
          console.error('Failed to poll status:', err);
        }
      }, 2000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [jobId, status, projectId, getAuthHeaders]);

  // Start export
  const handleExport = async () => {
    try {
      setExporting(true);
      setStatus('exporting');
      setProgress(0);
      setError(null);
      
      const res = await axios.post(`${API}/projects/${projectId}/editor/export?output_format=${format}`, {}, {
        headers: getAuthHeaders()
      });
      
      if (res.data.success) {
        setJobId(res.data.job.id);
        // Real polling will be handled by the useEffect above
      }
    } catch (err) {
      setStatus('failed');
      setError(err.response?.data?.detail || 'Failed to start export');
      setExporting(false);
    }
  };

  // Download the exported file
  const handleDownload = () => {
    if (outputUrl) {
      const link = document.createElement('a');
      link.href = outputUrl.startsWith('http') ? outputUrl : `${getApiBaseUrl()}${outputUrl}`;
      link.download = `${projectName || 'export'}.${format}`;
      link.click();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="w-5 h-5 text-indigo-400" />
            Export Project
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Export your project as a single video file
          </DialogDescription>
        </DialogHeader>
        
        {status === 'idle' && (
          <>
            {/* Project summary */}
            <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Duration</span>
                <span className="text-white">{formatDuration(totalDuration)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Shots</span>
                <span className="text-white">{clipCount}</span>
              </div>
            </div>
            
            {/* Format selection */}
            <div className="space-y-3">
              <Label className="text-sm text-zinc-300">Output Format</Label>
              <RadioGroup value={format} onValueChange={setFormat} className="space-y-2">
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors cursor-pointer">
                  <RadioGroupItem value="mp4" id="mp4" />
                  <Label htmlFor="mp4" className="flex-1 cursor-pointer">
                    <div className="font-medium">MP4 (H.264)</div>
                    <div className="text-xs text-zinc-500">Best for web, social media, and general use</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors cursor-pointer">
                  <RadioGroupItem value="prores" id="prores" />
                  <Label htmlFor="prores" className="flex-1 cursor-pointer">
                    <div className="font-medium">ProRes 422</div>
                    <div className="text-xs text-zinc-500">Professional quality, larger file size</div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Duration warning */}
            {totalDuration > 1800000 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200">
                  This is a long video ({formatDuration(totalDuration)}). Export may take several minutes.
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleExport} className="bg-indigo-600 hover:bg-indigo-700">
                <Download className="w-4 h-4 mr-2" />
                Start Export
              </Button>
            </DialogFooter>
          </>
        )}
        
        {status === 'exporting' && (
          <div className="py-8 space-y-6">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium">Exporting...</h3>
              <p className="text-sm text-zinc-400 mt-1">
                This may take a few minutes for longer videos
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Progress</span>
                <span className="text-white">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            
            <p className="text-xs text-zinc-500 text-center">
              You can close this dialog - the export will continue in the background
            </p>
          </div>
        )}
        
        {status === 'completed' && (
          <div className="py-8 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-medium">Export Complete!</h3>
              <p className="text-sm text-zinc-400 mt-1">
                Your video is ready to download
              </p>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">
                Close
              </Button>
              <Button onClick={handleDownload} className="bg-emerald-600 hover:bg-emerald-700">
                <Download className="w-4 h-4 mr-2" />
                Download {format.toUpperCase()}
              </Button>
            </DialogFooter>
          </div>
        )}
        
        {status === 'failed' && (
          <div className="py-8 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-medium">Export Failed</h3>
              <p className="text-sm text-red-400 mt-1">
                {error || 'An error occurred during export'}
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">
                Close
              </Button>
              <Button onClick={() => { setStatus('idle'); setError(null); }} className="bg-indigo-600 hover:bg-indigo-700">
                Try Again
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
